import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useToastManager } from '@natter-co/design-system';
import { isBackendErrorAlert, removeAppMessage, selectToastAlertsByContext } from 'modules/alerts/state';
import { useAppDispatch, useAppSelector } from 'store';

import { toBackendErrorToastOptions, toToastOptions } from './toToastOptions';

import type { AlertContext, BackendErrorAlertData, SnackBarAlertData } from 'modules/alerts/state';

type Props = {
  context: AlertContext;
};

type ToastAlert = SnackBarAlertData | BackendErrorAlertData;

/**
 * Draws the Redux snackbar alerts of one context as DS 2.0 toasts. Redux stays the source of truth:
 * the alert's id is the toast's id, so a re-dispatched alert replaces its toast in place, and a toast
 * that closes by itself, from its button or by swipe removes its alert so the slice's
 * de-duplication and the live hand-raised lookup stay correct.
 *
 * The APP context also draws backend errors, which the legacy path shows as the `ApiErrorAlert`
 * ribbon. On phones that ribbon sits at the top, exactly where APP toasts now stack.
 */
export const AlertsToastBridge = ({ context }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');
  const toast = useToastManager();
  const alerts: ToastAlert[] = useAppSelector((state) => selectToastAlertsByContext(state, context));
  const shown = useRef(new Map<string, ToastAlert>());
  const current = useRef(new Map<string, ToastAlert>());

  current.current = new Map(alerts.map((alert) => [alert.id, alert]));

  useEffect(() => {
    alerts.forEach((alert) => {
      if (shown.current.get(alert.id) === alert) {
        return;
      }
      shown.current.set(alert.id, alert);
      toast.add({
        ...(isBackendErrorAlert(alert) ? toBackendErrorToastOptions(t) : toToastOptions(alert, dispatch)),
        id: alert.id,
        // A toast closed by the bridge, or replaced by a newer alert with the same id, must not
        // remove the alert that is now in the store.
        onClose: () => {
          if (shown.current.get(alert.id) === alert) {
            shown.current.delete(alert.id);
          }
          if (current.current.get(alert.id) === alert) {
            dispatch(removeAppMessage(alert.id));
          }
        },
      });
    });

    [...shown.current.keys()].forEach((id) => {
      if (!current.current.has(id)) {
        shown.current.delete(id);
        toast.close(id);
      }
    });
  }, [alerts, dispatch, toast, t]);

  return null;
};
